import { memo, useMemo, useState } from 'react';

import { ActionCardProps } from '../../../types/ActionCardProps';
import { Actions } from '../../Actions/Actions';
import { Box } from '../../Box/Box';
import { Chips } from '../../Chips/Chips';
import { Footer } from '../../Footer/Footer';
import { Icon } from '../../Icon/Icon';
import { Layout } from '../../Layout/Layout';
import { Logo } from '../../Logo/Logo';
import { MarkedText } from '../../MarkedText/MarkedText';
import { StatusText } from '../../StatusText/StatusText';
import { TruncateLines } from '../../Text/subcomponents/TruncateLines';

export const ActionCard = memo(
  ({
    actions,
    chips,
    description,
    descriptionLimit,
    hoverAction,
    icon,
    height,
    logo,
    name,
    search,
    size = 'm',
    statusText,
    title,
    titleLimit,
    onClick,
  }: ActionCardProps) => {
    const [isHovered, setIsHovered] = useState(false);

    const componentStyles = {
      base: {
        border: 'border',
        borderRadius: 'm',
        minWidth: '100%',
        position: 'relative',
        width: '100%',
        '&:hover': {
          boxShadow: Boolean(onClick) ? 'raised' : 'none',
        },
      },
    };

    const body = useMemo(() => {
      const padding = size === 's' ? 2 : 4;
      return size === 'xs' ? (
        <Layout align="center" justify="space-between" px={4} py={3} w="100%">
          <Layout spacing={logo ? 3 : 2}>
            {logo ? <Logo provider={logo} /> : icon && <Icon {...icon} />}
            <TruncateLines limit={titleLimit} variant="body-medium">
              <MarkedText text={title} search={search} />
            </TruncateLines>
          </Layout>
          {actions && <Actions actions={actions} />}
        </Layout>
      ) : (
        <Layout direction="column" flex="auto" h="100%" p={padding} w="100%">
          <Layout direction="column" flex="auto" h={height} spacing={6}>
            <Layout
              align="center"
              justify="space-between"
              spacing={logo ? 3 : 2}
              minW={0}
              w="100%"
            >
              <Layout
                align="center"
                spacing={logo ? 3 : 2}
                minW={0}
                flex="auto"
              >
                {logo ? <Logo provider={logo} /> : icon && <Icon {...icon} />}
                <TruncateLines
                  limit={titleLimit}
                  variant="body-bold"
                  fontSize="16"
                  lineHeight="24px"
                >
                  <MarkedText text={title} search={search} />
                </TruncateLines>
              </Layout>
              {hoverAction && isHovered && (
                <Box
                  flex="none"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                  }}
                  onMouseDown={(e: React.MouseEvent) => {
                    e.stopPropagation();
                  }}
                >
                  <Actions
                    actions={[
                      {
                        ...hoverAction,
                        onClick: () => {
                          hoverAction.onClick?.();
                        },
                      },
                    ]}
                  />
                </Box>
              )}
            </Layout>
            {chips && <Chips chips={chips} />}
            {description && (
              <TruncateLines limit={descriptionLimit} preset="description">
                <MarkedText text={description} search={search} />
              </TruncateLines>
            )}
            <Box />
          </Layout>
          <Footer
            isEmbedded
            actions={actions}
            enableBorder={false}
            leftContent={
              statusText ? <StatusText {...statusText} /> : undefined
            }
            size="s"
          />
        </Layout>
      );
    }, [
      actions,
      description,
      height,
      hoverAction,
      icon,
      isHovered,
      logo,
      search,
      size,
      statusText,
      title,
      titleLimit,
      chips,
      descriptionLimit,
    ]);
    return (
      <Layout
        aria-label={name}
        as="li"
        bg="background"
        role="listbox"
        styles={[componentStyles.base]}
        h="100%"
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {body}
      </Layout>
    );
  },
);
